import {useMutation} from '@github-ui/react-query'
import {testIdProps} from '@github-ui/test-id-props'
import {BookIcon, KebabHorizontalIcon, LockIcon, PulseIcon, SyncIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Button, IconButton, Label, Link, RelativeTime, Spinner} from '@primer/react'
import {Blankslate, SkeletonText} from '@primer/react/experimental'
import {memo, useCallback, useEffect, useMemo, useState} from 'react'

import {apiGenerateRecap} from '../../api/scribe/api-generate-recap'
import {useMarkdownPreview} from '../../hooks/use-markdown-preview'
import {useRecapsQuery} from '../../queries/use-recaps-query'
import {useProjectRouteParams} from '../../router/use-project-route-params'
import {SanitizedMarkdownViewer} from '../common/sanitized-markdown-viewer'
import contentStyles from './content.module.css'
import {SidePanelToolbar} from './header'
import styles from './recap-content.module.css'

export const RecapContent = memo(function RecapContent() {
  const projectRouteParams = useProjectRouteParams()
  const {
    data: recaps,
    isLoading,
    error,
    refetch,
  } = useRecapsQuery({
    variables: projectRouteParams,
    refetchInterval: 3000, // Poll every 3 seconds
  })
  const [selectedTab, setSelectedTab] = useState<'latest' | 'archive'>('latest')
  const {html: reportHtml, loadPreview} = useMarkdownPreview()
  const [generationStartTime, setGenerationStartTime] = useState<number | null>(null)
  const [selectedRecapId, setSelectedRecapId] = useState<number | null>(null)

  const latestRecap = useMemo(() => {
    if (selectedRecapId !== null && recaps) {
      const found = recaps.find(recap => recap.id === selectedRecapId)
      return found || null
    }
    return recaps && recaps.length ? recaps[0] : null
  }, [selectedRecapId, recaps])
  const hasValidData =
    latestRecap &&
    ((latestRecap.insights && latestRecap.insights.length > 0) || (latestRecap.report && latestRecap.report.content))

  useEffect(() => {
    if (latestRecap?.report?.content) {
      const unescapedContent = latestRecap.report.content.replace(/\\n/g, '\n')
      loadPreview(unescapedContent)
    }
  }, [latestRecap, loadPreview])

  const {mutate: generateRecap, isPending: isGeneratingRecap} = useMutation({
    mutationFn: apiGenerateRecap,
    onSuccess: () => refetch(),
  })

  const handleGenerateRecap = useCallback(() => {
    setGenerationStartTime(Date.now())
    generateRecap(projectRouteParams)
  }, [generateRecap, projectRouteParams])

  const handleViewRecap = useCallback((recapId: number) => {
    setGenerationStartTime(null)
    setSelectedRecapId(recapId)
    setSelectedTab('latest')
  }, [])

  const handleSelectLatestTab = useCallback(() => {
    setSelectedTab('latest')
    setSelectedRecapId(null)
  }, [])

  const handleSelectArchiveTab = useCallback(() => {
    setSelectedTab('archive')
  }, [])

  const isRecapRecent = useMemo(() => {
    if (!latestRecap?.created_at) return false
    if (!generationStartTime) return false
    const recapTime = new Date(latestRecap.created_at).getTime()
    return recapTime > generationStartTime
  }, [latestRecap, generationStartTime])

  const showLoadingSpinner = generationStartTime !== null && !isRecapRecent

  const noRecapsAvailable = !isLoading && !error && !hasValidData && !showLoadingSpinner

  return (
    <div className={contentStyles.infoPanelContainer} {...testIdProps('side-panel-recap-content')}>
      <div className={contentStyles.infoPanelContent}>
        <div className={contentStyles.infoPanelHeader}>
          <SidePanelToolbar showCloseButton>
            <div className={styles.infoPanelLinks}>
              <Label variant="accent">Staff</Label>
              <Link
                muted
                href="https://gh.io/copilot-reporting-project-feedback"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Staff feedback"
                className={styles.feedbackLink}
              >
                Feedback
              </Link>
            </div>
          </SidePanelToolbar>
          <div className={contentStyles.headerSpacer} />
          <h2>Recap</h2>
        </div>

        <div role="tablist" aria-label="Recap tabs" className={styles.tabList}>
          <Button
            role="tab"
            aria-selected={selectedTab === 'latest'}
            aria-controls="latest-panel"
            id="latest-tab"
            onClick={handleSelectLatestTab}
            variant={selectedTab === 'latest' ? 'default' : 'invisible'}
            className={selectedTab === 'latest' ? styles.tabActive : styles.tab}
            tabIndex={selectedTab === 'latest' ? 0 : -1}
          >
            Latest
          </Button>
          <Button
            role="tab"
            aria-selected={selectedTab === 'archive'}
            aria-controls="archive-panel"
            id="archive-tab"
            onClick={handleSelectArchiveTab}
            variant={selectedTab === 'archive' ? 'default' : 'invisible'}
            className={selectedTab === 'archive' ? styles.tabActive : styles.tab}
            tabIndex={selectedTab === 'archive' ? 0 : -1}
          >
            Archive
          </Button>
        </div>

        {selectedTab === 'latest' && (
          <div role="tabpanel" id="latest-panel" aria-labelledby="latest-tab">
            {isLoading && <div>Loading recaps...</div>}

            {error && <div>Failed to load recaps</div>}

            {showLoadingSpinner && (
              <div>
                <Button disabled leadingVisual={<Spinner size="small" />} className={styles.generateRecapLargeButton}>
                  Generating recap...
                </Button>

                <div className={styles.insightCard}>
                  <div className={styles.skeletonText}>
                    <SkeletonText lines={3} aria-label="Generating recap" />
                  </div>
                  <div className={styles.skeletonText}>
                    <SkeletonText lines={3} aria-hidden="true" />
                  </div>
                  <div className={styles.skeletonText}>
                    <SkeletonText lines={3} aria-hidden="true" />
                  </div>
                </div>
              </div>
            )}

            {hasValidData && !showLoadingSpinner && (
              <div className={styles.recapsContent}>
                {latestRecap.created_at && (
                  <div className={styles.recapHeader}>
                    <h2 className={styles.insightTitle}>
                      Week of{' '}
                      {new Date(latestRecap.created_at).toLocaleDateString('en-US', {month: 'short', day: 'numeric'})}
                      <span className={styles.recapDate}>
                        Generated <RelativeTime datetime={latestRecap.created_at} /> · <LockIcon size={12} /> Only
                        visible to you
                      </span>
                    </h2>
                    <div className={styles.recapActions}>
                      <IconButton
                        aria-label="Regenerate recap"
                        icon={SyncIcon}
                        size="small"
                        onClick={handleGenerateRecap}
                        disabled={isGeneratingRecap}
                      />
                    </div>
                  </div>
                )}

                {latestRecap.insights && latestRecap.insights.length > 0 && (
                  <div className={styles.insightCard}>
                    <div className={styles.insightHeader}>
                      <h3 className={styles.widgetTitle}>Insights</h3>
                    </div>
                    <ul className={styles.insightList}>
                      {latestRecap.insights.map(insight => (
                        <li key={insight.id} className={styles.insightItem}>
                          <PulseIcon size={16} aria-hidden="true" className={styles.insightIcon} />
                          <span className={styles.insightText}>{insight.content}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {latestRecap.report && reportHtml && (
                  <div className={styles.insightCard}>
                    <div className={styles.reportContent}>
                      <SanitizedMarkdownViewer unverifiedHTML={reportHtml} openLinksInNewTab />
                    </div>
                  </div>
                )}
              </div>
            )}

            {noRecapsAvailable && (
              <div className={styles.insightCard}>
                <Blankslate>
                  <Blankslate.Visual>
                    <BookIcon size="medium" />
                  </Blankslate.Visual>
                  <Blankslate.Heading>Turn recent activity into insights</Blankslate.Heading>
                  <Blankslate.Description>
                    Get a quick summary and actionable insights from recent work in this project.
                  </Blankslate.Description>
                  <Blankslate.PrimaryAction
                    onClick={handleGenerateRecap}
                    disabled={isGeneratingRecap}
                    className={styles.generateRecapLargeButton}
                  >
                    {isGeneratingRecap ? 'Generating recap...' : 'Generate recap'}
                  </Blankslate.PrimaryAction>
                </Blankslate>
              </div>
            )}
          </div>
        )}

        {selectedTab === 'archive' && (
          <div role="tabpanel" id="archive-panel" aria-labelledby="archive-tab">
            {isLoading && <div>Loading recaps...</div>}

            {error && <div>Failed to load recaps</div>}

            {!isLoading && !error && recaps && recaps.length > 0 ? (
              <div className={styles.archiveContent}>
                {recaps.map(recap => (
                  <div key={recap.id} className={styles.archiveItem} {...testIdProps('archive-item')}>
                    <div>
                      <h3 className={styles.archiveTitle}>
                        Week of{' '}
                        {new Date(recap.created_at || '').toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </h3>
                      <div className={styles.archiveDetails}>
                        {recap.description ? recap.description : 'No description available'}
                      </div>
                    </div>
                    <ActionMenu>
                      <ActionMenu.Anchor>
                        <IconButton icon={KebabHorizontalIcon} aria-label="Recap actions" size="small" />
                      </ActionMenu.Anchor>
                      <ActionMenu.Overlay>
                        <ActionList>
                          <ActionList.Item onSelect={() => handleViewRecap(recap.id)}>View Recap</ActionList.Item>
                        </ActionList>
                      </ActionMenu.Overlay>
                    </ActionMenu>
                  </div>
                ))}
              </div>
            ) : (
              !isLoading && !error && <div>No archived recaps available</div>
            )}
          </div>
        )}
      </div>
    </div>
  )
})

try{ RecapContent.displayName ||= 'RecapContent' } catch {}